/* Types of the freight cost xlsx file, for both exporting and importing freight costs */

import { XlsxRow, XlsxWritableCellValue } from '../xlsx';

/** One freight cost band of a factory (freightCost up to endDistance km) */
export type FreightCostBand = { endDistance: number; freightCost: number };

/**
 * The bands of one freight cost type.
 * It always holds at least one band: a type without a cost is simply not here.
 */
export type FreightCostsByCostType = { type: string; costs: FreightCostBand[] };

/**
 * The own freight costs of one factory, the way both import and export handle them:
 * the export writes a column per type, the import reads one back.
 */
export type FactoryFreightCosts = {
  // identifier (shortName) of the factory
  factoryId: string;
  types: FreightCostsByCostType[];
};

/** One row of an exported sheet: nothing but text and numbers can be written into a file */
export type ExportedSheetRow = XlsxRow<XlsxWritableCellValue>;

/** What the validation needs to know about the stored data */
export type FreightCostImportContext = {
  // identifiers (shortNames) of every factory (the active and the inactive ones too)
  factoryShortNames: string[];
};

/** The kinds of problem an uploaded file can hold */
export enum FreightCostImportErrorType {
  duplicatedDistance = 'duplicatedDistance',
  duplicatedFactory = 'duplicatedFactory',
  duplicatedTypeHeader = 'duplicatedTypeHeader',
  emptyFile = 'emptyFile',
  emptyTypeColumn = 'emptyTypeColumn',
  emptyTypeHeader = 'emptyTypeHeader',
  invalidCost = 'invalidCost',
  invalidDistance = 'invalidDistance',
  invalidPayload = 'invalidPayload',
  missingDistance = 'missingDistance',
  missingDistanceHeader = 'missingDistanceHeader',
  missingTypeColumns = 'missingTypeColumns',
  nothingToImport = 'nothingToImport',
  tooManyRows = 'tooManyRows',
  unknownFactory = 'unknownFactory',
  unknownTypeHeader = 'unknownTypeHeader'
}

/** One problem found in the uploaded file, addressed to the user */
export type FreightCostImportError = {
  type: FreightCostImportErrorType;
  // message to show to the user
  message: string;
  // name of the sheet the problem belongs to
  sheet?: string;
  // excel reference of the cell the problem belongs to, e.g. "C7"
  cell?: string;
};

/** What the validation of an uploaded file gives back */
export type FreightCostImportValidationResult = {
  // every problem found (not only the first one)
  errors: FreightCostImportError[];
  /**
   * The freight costs to save.
   * Only to be used when there is no error, since any error means the whole file is rejected.
   * Factories with an empty sheet are left out: they stay untouched.
   * When there is no error, it holds at least one factory.
   */
  factories: FactoryFreightCosts[];
};
